import React from 'react';
import './PickupCard.css';

/**
 * Reusable pickup card component for YouTube, note, Spotify, and event content
 * Used in HomePage Pick Up section and NewGraduatePage Event/Pick Up sections
 */
const PickupCard = ({
  variant = 'default', // 'default', 'compact', 'featured'
  type = 'youtube', // 'youtube', 'note', 'spotify', 'event'
  header,
  thumbnail,
  text,
  date,
  link = '#',
  showLinkIcon = true
}) => {
  const renderMediaIcon = (iconType) => {
    const icons = {
      youtube: (
        <svg className="pickup-media-icon pickup-youtube" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
      note: (
        <svg className="pickup-media-icon pickup-note" viewBox="0 0 24 24" fill="currentColor">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <text x="12" y="16" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">n</text>
        </svg>
      ),
      x: (
        <svg className="pickup-media-icon pickup-x" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      spotify: (
        <svg className="pickup-media-icon pickup-spotify" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
        </svg>
      )
    };
    return icons[iconType] || icons.youtube;
  };

  const renderThumbnail = () => {
    if (thumbnail.type === 'image' && thumbnail.src) {
      return (
        <img
          src={thumbnail.src}
          alt={thumbnail.alt || 'Thumbnail'}
          className="pickup-thumbnail-image"
          onError={(e) => {
            console.error('Failed to load image:', e.target.src);
            // Fallback: try without query parameters if the URL has them
            const currentSrc = e.target.src;
            if (currentSrc.includes('?') && !e.target.dataset.retried) {
              e.target.dataset.retried = 'true';
              e.target.src = currentSrc.split('?')[0];
            }
          }}
        />
      );
    }

    if (thumbnail.type === 'svg' || thumbnail.style === 'ng-youtube-thumb' || thumbnail.style === 'ng-youtube-thumb-2') {
      return (
        <div className={`pickup-thumbnail-placeholder ${thumbnail.style || ''}`}>
          <svg viewBox="0 0 24 24" fill="none">
            <polygon points="10,8 16,12 10,16" fill="currentColor" />
          </svg>
        </div>
      );
    }

    if (thumbnail.type === 'note-style') {
      return (
        <div className="pickup-thumbnail-placeholder pickup-note-style">
          {thumbnail.text ? (
            <>
              <span className="pickup-note-placeholder-text">
                {thumbnail.text}
                {thumbnail.subtext && <><br />{thumbnail.subtext}</>}
              </span>
              {thumbnail.subtitle && <span className="pickup-note-placeholder-sub">{thumbnail.subtitle}</span>}
            </>
          ) : (
            <>
              <span className="pickup-note-placeholder-text">新卒で学ぶデザイン思考と<br />ビジネススキル</span>
              <span className="pickup-note-placeholder-sub">Design Thinking</span>
            </>
          )}
        </div>
      );
    }

    if (thumbnail.type === 'x-style') {
      return (
        <div className="pickup-thumbnail-placeholder pickup-x-style">
          <div className="pickup-x-placeholder-content">
            <svg className="pickup-x-logo" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <span className="pickup-x-placeholder-text">@FLARETECH_saiyo</span>
          </div>
        </div>
      );
    }

    if (thumbnail.type === 'spotify-style') {
      return (
        <div className="pickup-thumbnail-placeholder pickup-spotify-style">
          <div className="pickup-spotify-placeholder-content">
            <span className="pickup-spotify-title">新卒採用<br />トークラジオ</span>
            <div className="pickup-spotify-illustration">
              <svg viewBox="0 0 60 60" fill="none">
                <circle cx="30" cy="35" r="15" fill="#4A90D9" />
                <circle cx="30" cy="25" r="8" fill="#FFD4B8" />
                <rect x="22" y="40" width="16" height="12" fill="#4A90D9" />
              </svg>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <a href={link} className="pickup-card" data-variant={variant} target="_blank" rel="noopener noreferrer">
      <div className="pickup-card-header">
        {renderMediaIcon(type)}
        <span>{header}</span>
      </div>
      <div className={`pickup-card-thumbnail pickup-${type}-thumb ${thumbnail.style || ''}`}>
        {renderThumbnail()}
      </div>
      <div className="pickup-card-content">
        <p className="pickup-card-text">
          {text}
          {showLinkIcon && (
            <span className="pickup-link-icon">
              <svg viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M5.845 2.22a.75.75 0 0 1 1.06 0L9.23 4.543c.41.41.41 1.075 0 1.485L6.905 8.352a.75.75 0 0 1-1.06-1.061l1.25-1.251H.75a.75.75 0 0 1 0-1.5h6.354l-1.26-1.26a.75.75 0 0 1 0-1.06Z" />
              </svg>
            </span>
          )}
        </p>
        <p className="pickup-card-date">{date}</p>
      </div>
    </a>
  );
};

export default PickupCard;
